import { useState, useEffect } from 'react'
import type { StateWorkspaceSummary } from '@/lib/apiTypes'
import { usePlanPolicyConfig, savePlanPolicyConfig } from '@/lib/hooks'
import { useToast } from '@/context/ToastContext'
import { SkeletonLine } from '@/shared/components/Skeleton'

export function PlanGateConfigEditor({ orgId, workspace }: { orgId: string; workspace: StateWorkspaceSummary }) {
  const { showToast } = useToast()
  const config = usePlanPolicyConfig(orgId, workspace.name, true)
  const [maxDestroys, setMaxDestroys] = useState<string>('')
  const [denylist, setDenylist] = useState<string>('')
  const [requireApproval, setRequireApproval] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const c = config.data?.config
    setMaxDestroys(c?.max_destroys != null ? String(c.max_destroys) : '')
    setDenylist((c?.destroy_denylist ?? []).join('\n'))
    setRequireApproval(c?.require_approval ?? false)
  }, [config.data])

  async function handleSave() {
    const trimmed = maxDestroys.trim()
    let parsedMax: number | null = null
    if (trimmed !== '') {
      const n = Number(trimmed)
      if (!Number.isInteger(n) || n < 0) { showToast('Destroy threshold must be a non-negative integer (or blank).', 'error'); return }
      parsedMax = n
    }
    const types = denylist.split(/[\n,]/).map((t) => t.trim()).filter(Boolean)
    setSaving(true)
    try {
      await savePlanPolicyConfig(orgId, workspace.name, { max_destroys: parsedMax, destroy_denylist: types, require_approval: requireApproval })
      showToast('Gate config saved for this workspace.', 'success')
      config.refetch()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'grid', gap: 10, background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '12px 14px' }}>
      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>// gate config — this workspace (overrides the org default)</div>
      {config.loading ? <SkeletonLine height={20} /> : (
        <>
          <label className="stack-4">
            <span style={{ fontSize: '0.7rem', color: 'var(--text2)' }}>Destroy threshold (blank = no limit)</span>
            <input
              value={maxDestroys}
              onChange={(e) => setMaxDestroys(e.target.value)}
              inputMode="numeric"
              placeholder="e.g. 3"
              style={{ padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.72rem', maxWidth: 160 }}
            />
          </label>
          <label className="stack-4">
            <span style={{ fontSize: '0.7rem', color: 'var(--text2)' }}>Destroy deny-list (one resource type per line, e.g. aws_db_instance)</span>
            <textarea
              value={denylist}
              onChange={(e) => setDenylist(e.target.value)}
              rows={3}
              placeholder={'aws_db_instance\naws_s3_bucket'}
              style={{ padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 5, color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.72rem', resize: 'vertical' }}
            />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.72rem', color: 'var(--text2)' }}>
            <input type="checkbox" checked={requireApproval} onChange={(e) => setRequireApproval(e.target.checked)} />
            Require manual approval for every plan
          </label>
          <div>
            <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
              {saving ? <span className="spinner" /> : 'Save gate config'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
